
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface AccessibleBreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

const AccessibleBreadcrumb: React.FC<AccessibleBreadcrumbProps> = ({ items, className = '' }) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  const homeLabel = currentLanguage === 'de' ? 'Startseite' : 
                  currentLanguage === 'fr' ? 'Accueil' : 
                  currentLanguage === 'it' ? 'Home' : 'Home';
  
  return (
    <Breadcrumb className={`mb-6 ${className}`}>
      <BreadcrumbList aria-label={t.breadcrumbNavigation || 'Breadcrumb navigation'}>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link 
              to={`/${currentLanguage}`}
              aria-label={homeLabel}
            >
              {homeLabel}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator aria-hidden="true">
          <ChevronRight className="h-4 w-4" />
        </BreadcrumbSeparator>
        
        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;
          
          return isLastItem ? (
            <BreadcrumbItem key={`breadcrumb-item-${index}`}>
              <BreadcrumbPage aria-current="page">{item.label}</BreadcrumbPage>
            </BreadcrumbItem>
          ) : (
            <React.Fragment key={`breadcrumb-item-${index}`}>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  {item.href ? (
                    <Link to={item.href}>{item.label}</Link>
                  ) : (
                    <span>{item.label}</span>
                  )}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator aria-hidden="true">
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default AccessibleBreadcrumb;
